/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import {
  type ProductShelfACESResultsModel,
  type ProductShelfResultsModel,
} from '@/api/types/browse-search-types';
import { FlexGrid } from '@/components/FlexGrid/FlexGrid';
import ShopByPaginationStyleLinks from '@/components/ShopByPaginationStyleLinks';
import { useCategoryYears } from '@/features/category/api/getCategoryYears';
import { usePageType } from '@/features/category/api/getPageType';
import { useProductShelfACESResults } from '@/features/category/api/getProductShelfACESResults';
import { useProductShelfResults } from '@/features/category/api/getProductShelfResults';
import { useCatalogConfigs } from '@/features/common/api/getCatalogConfig';
import { useFeatureFlag } from '@/features/globalConfig';
import { getACESShopByType, getShopByType } from '@/features/shelf/utils/getShopByType';
import { ShopByModelList } from '@/features/shopByModel';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import dynamic from 'next/dynamic';

const LazyYearsTable = dynamic(
  () =>
    import('../../../shelf/components/YearsTable/YearsTable').then(({ YearsTable }) => ({
      default: YearsTable,
    })),
  { ssr: true }
);

export const CMSMakeModelYearRange = () => {
  const yearsTableEnabled = useFeatureFlag('YEARS_TABLE_ENABLED') === 'true';
  const catalogConfigAPIEnabled = useFeatureFlag('CATALOG_CONFIG_API_ENABLED') === 'true';

  const { acesEnabled, acesNonVehicleEndpointsEnabled } = useAcesFlags();
  const isACESEnabled = acesEnabled && acesNonVehicleEndpointsEnabled;
  const useShelfResultsFunction =
    acesEnabled && acesNonVehicleEndpointsEnabled
      ? useProductShelfACESResults
      : useProductShelfResults;
  const { data: productShelfResultsData, status: productsShelfStatus } = useShelfResultsFunction();

  const acesShelfResults = isACESEnabled
    ? (productShelfResultsData?.productShelfResults as ProductShelfACESResultsModel | undefined)
    : undefined;

  const productId = isACESEnabled
    ? acesShelfResults?.partGroupId ?? acesShelfResults?.partTerminologyId
    : (productShelfResultsData?.productShelfResults as ProductShelfResultsModel | undefined)
        ?.productId;

  const shopByType = productShelfResultsData?.productShelfResults
    ? isACESEnabled
      ? getACESShopByType(
          productShelfResultsData.productShelfResults as ProductShelfACESResultsModel
        )
      : getShopByType(productShelfResultsData.productShelfResults as ProductShelfResultsModel)
    : null;
  const { data: pageTypeData } = usePageType();

  const { data: catalogConfigsData } = useCatalogConfigs({
    itemId: productId ?? '',
    itemType: 'product',
    enabled: shopByType === 'ShopByModel',
  });

  const { data: yearsData } = useCategoryYears({
    enabled:
      productsShelfStatus === 'success' &&
      (shopByType === 'ShopByModel' || shopByType === 'ShopByYear'),
    year: productShelfResultsData?.productShelfResults?.year,
    acesOptions: {
      makeId: acesShelfResults?.makeId,
      modelId: acesShelfResults?.modelId,
      partGroupId: acesShelfResults?.partGroupId,
    },
  });

  const yearsTableProductFlag = catalogConfigAPIEnabled
    ? catalogConfigsData?.configs?.yearsTableEnabled
    : (productShelfResultsData?.productShelfResults as ProductShelfResultsModel | undefined)
        ?.yearsTableEnableFlag;

  return (
    <FlexGrid flexDirection="column" gap={4}>
      {shopByType === 'ShopByModel' && !!yearsData?.shopByMMYList.length && (
        <ShopByModelList
          data={{
            shopByMMYList: yearsData.shopByMMYList,
            canonicalPath: yearsData.canonicalPath,
            taxonomyPath: yearsData.taxonomyPath,
            pageType: yearsData.pageType,
            title: yearsData.title ?? '',
            desktop: true,
            tablet: true,
            mobile: true,
          }}
        />
      )}
      {shopByType === 'ShopByModel' && yearsTableEnabled && yearsTableProductFlag && (
        <LazyYearsTable
          productId={productId}
          make={
            acesEnabled
              ? undefined // TODO map ACES response
              : (productShelfResultsData?.productShelfResults as ProductShelfResultsModel).make ??
                undefined
          }
          model={
            acesEnabled
              ? undefined // TODO map ACES response
              : (productShelfResultsData?.productShelfResults as ProductShelfResultsModel).model ??
                undefined
          }
          hasAssociatedPartType={
            acesEnabled
              ? undefined
              : (productShelfResultsData?.productShelfResults as ProductShelfResultsModel)
                  .hasAssociatedPartType
          }
        />
      )}
      {shopByType === 'ShopByYear' && yearsData && !!yearsData.shopByMMYList.length && (
        <ShopByPaginationStyleLinks
          data={{
            title: yearsData.title ?? '',
            shopByPaginationStyleLinks: yearsData.shopByMMYList ?? [],
            canonicalPath: yearsData.canonicalPath,
            taxonomyPath: yearsData.taxonomyPath,
            pageType: yearsData.pageType
              ? (productShelfResultsData?.productShelfResults as ProductShelfResultsModel)
                  ?.pageType ?? pageTypeData?.pageType
              : undefined,
            desktop: true,
            tablet: true,
            mobile: true,
          }}
        />
      )}
    </FlexGrid>
  );
};
